/** Outcome of validating a report-viewer API response: the config row, or the alert message to show. */
export type ReportViewerConfigResult =
  | { ok: true; cfg: any }
  | { ok: false; error: string };

/** Minimal shape of the HTTP response consumed by {@link resolveReportViewerConfig}. */
export interface ReportViewerHttpResponse {
  status: number;
  body?: any;
}

/**
 * Validates a report-viewer API response and extracts its report configuration row.
 *
 * The body may be `{ result: [...] }` or a bare array; the first row is the configuration.
 * @param response - The HTTP response returned by the authorised-report-data request.
 * @param reportType - Report type identifier, used in the "no configuration" message.
 * @returns `ok: true` with the configuration row, or `ok: false` with the error message to display.
 */
// any: the report configuration row shape varies per report.
export function resolveReportViewerConfig(
  response: ReportViewerHttpResponse,
  reportType: string
): ReportViewerConfigResult {
  if (response.status !== 200 || !response.body) {
    return { ok: false, error: 'Invalid response from server.' };
  }

  const body = response.body;
  if (body.result && Array.isArray(body.result) && body.result.length > 0) {
    return { ok: true, cfg: body.result[0] };
  }
  if (Array.isArray(body) && body.length > 0) {
    return { ok: true, cfg: body[0] };
  }

  return { ok: false, error: `No report configuration available for ${reportType}.` };
}

/**
 * Resolves the report date range: the selected dates, or the current financial year to date.
 * @param startDate - Selected start date (any value the formatter accepts), if any.
 * @param endDate - Selected end date, if any.
 * @param formatLocalDate - Formats a date as a local `YYYY-MM-DD` string.
 * @returns The formatted start (`SD`) and end (`ED`) dates; defaults are 1 April of the current
 * financial year and today.
 */
export function resolveReportDateRange<T>(
  startDate: T | null | undefined,
  endDate: T | null | undefined,
  formatLocalDate: (date: T | Date | null | undefined) => string
): { SD: string; ED: string } {
  const today = new Date();

  const SD = startDate
    ? formatLocalDate(startDate)
    : formatLocalDate(
        today.getMonth() < 3
          ? new Date(today.getFullYear() - 1, 3, 1)
          : new Date(today.getFullYear(), 3, 1)
      );

  const ED = endDate
    ? formatLocalDate(endDate)
    : formatLocalDate(today);

  return { SD, ED };
}
